import React, { useState, useEffect } from "react";
import {
  fetchAppointments,
  deleteAppointment,
  updateAppointment,
  getShiftSetting,
  setShiftSetting,
  getShiftSettings,
  updateShiftSettings,
  getPlannedShifts,
  addPlannedShift,
  removePlannedShift,
  clearPlannedShifts,
  DEFAULT_SHIFT_SETTINGS,
} from "../supabase";
//import "../AppStyles.css";
//import "../AdminPanel.css"; // Using Tailwind CSS instead
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabase";
import AdminCreateAppointment from './AdminCreateAppointment';
import InstallPWA from './InstallPWA';
import PushNotificationToggle from './PushNotificationToggle';

function AdminPanel({ onLogout }) {
  const [appointments, setAppointments] = useState([]);
  const [error, setError] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [formData, setFormData] = useState({
    time: "",
    service: "",
    date: "",
  });
  const [blockedDates, setBlockedDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [availableTimes, setAvailableTimes] = useState([]);
  const [bookedTimes, setBookedTimes] = useState([]);
  const [bookedTimeDetails, setBookedTimeDetails] = useState(new Map());
  const [availableDates, setAvailableDates] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshSuccess, setRefreshSuccess] = useState(false);
  // Generiert Zeitslots von 09:00 bis 19:30 im 30-Minuten-Takt
  const generateTimeSlots = () => {
    const slots = [];
    let hour = 9;
    let minute = 0;
    while (hour < 19 || (hour === 19 && minute <= 30)) {
      const formatted = `${hour.toString().padStart(2, "0")}:${minute
        .toString()
        .padStart(2, "0")}`;
      slots.push(formatted);
      minute += 30;
      if (minute >= 60) {
        hour += 1;
        minute -= 60;
      }
    }
    return slots;
  };

  const allTimes = generateTimeSlots();
  const navigate = useNavigate();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isBlockedOpen, setIsBlockedOpen] = useState(false);
  // State für Zeitblock-Sperrung
  const [selectedTimeBlock, setSelectedTimeBlock] = useState("");
  const [blockedTimeSlots, setBlockedTimeSlots] = useState([]);
  const [unblockDialog, setUnblockDialog] = useState({ open: false, slot: null });
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  // Schichtsystem State
  const [currentShift, setCurrentShift] = useState("normal");
  const [isShiftOpen, setIsShiftOpen] = useState(false);
  const [shiftSaving, setShiftSaving] = useState(false);
  const [shiftSaveSuccess, setShiftSaveSuccess] = useState(false);
  // Benutzerdefinierte Schichteinstellungen
  const [shiftSettings, setShiftSettings] = useState(DEFAULT_SHIFT_SETTINGS);
  const [editingShift, setEditingShift] = useState(null); // Welche Schicht wird bearbeitet
  const [editSchedule, setEditSchedule] = useState({}); // Schedule für alle Tage
  // Schichtplan (Array von geplanten Schichten)
  const [plannedShifts, setPlannedShifts] = useState([]); // [{ shift: "...", startDate: "...", endDate: "..." }, ...]
  const [showDatePicker, setShowDatePicker] = useState(null); // Welche Schicht braucht Datum?
  const [selectedStartDate, setSelectedStartDate] = useState("");
  const [selectedEndDate, setSelectedEndDate] = useState("");
  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const checkAdminAccess = async () => {
      try {
        const {
          data: { user },
          error: authError,
        } = await supabase.auth.getUser();

        if (!user || authError) {
          setError("Benutzer ist nicht authentifiziert.");
          navigate("/login");
          return;
        }

        const { data: userInfo, error } = await supabase
          .from("users")
          .select("role")
          .eq("id", user.id)
          .single();

        if (error) {
          setError("Benutzerrolle konnte nicht geladen werden.");
          navigate("/login");
        } else if (userInfo.role !== "admin") {
          setError("Sie haben keine Berechtigung, auf diese Seite zuzugreifen.");
          navigate("/login");
        } else {
          setUserRole("admin");
        }
      } catch (err) {
        setError("Fehler beim Authentifizieren des Benutzers.");
        navigate("/login");
      }
    };

    checkAdminAccess();
    fetchAppointmentsFromDB();
    fetchBlockedDates();
    generateAvailableDates();
    loadShiftSetting();

    // Realtime Listener fuer Appointments
    const channel = supabase
      .channel('admin-notifications')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'appointments' }, (payload) => {
        const { eventType, new: newRecord, old: oldRecord } = payload;
        const record = newRecord || oldRecord;
        if (record && (record.name === 'BLOCKED' || record.service === 'BLOCKED')) return;

        let type, title, detail;
        const time = new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
        const date = record?.date ? new Date(record.date).toLocaleDateString('de-DE') : '';

        if (eventType === 'INSERT') {
          type = 'new';
          title = 'Neuer Termin';
          detail = `${record.name} - ${date} ${record.time}`;
        } else if (eventType === 'UPDATE') {
          type = 'update';
          title = 'Termin geaendert';
          detail = `${record.name} - ${date} ${record.time}`;
        } else if (eventType === 'DELETE') {
          type = 'delete';
          title = 'Termin geloescht';
          detail = `${oldRecord.name} - ${new Date(oldRecord.date).toLocaleDateString('de-DE')} ${oldRecord.time}`;
        }

        if (title) {
          setNotifications((prev) => [{ id: Date.now(), type, title, detail, time, read: false }, ...prev].slice(0, 50));
          setUnreadCount((prev) => prev + 1);
          fetchAppointmentsFromDB();
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [navigate]);

  // Schichteinstellung laden
  const loadShiftSetting = async () => {
    try {
      const { shift, error } = await getShiftSetting();
      if (!error) {
        setCurrentShift(shift);
      }
      // Lade auch die benutzerdefinierten Einstellungen
      const { settings } = await getShiftSettings();
      if (settings) {
        setShiftSettings(settings);
      }
      // Lade Schichtplan
      const { plannedShifts: loaded } = await getPlannedShifts();
      console.log("Geladener Schichtplan:", loaded);
      setPlannedShifts(loaded || []);
    } catch (err) {
      console.error("Fehler beim Laden der Schichteinstellung:", err);
    }
  };

  // Schicht zum Bearbeiten öffnen
  const openEditShift = (shiftKey) => {
    const config = shiftSettings[shiftKey] || DEFAULT_SHIFT_SETTINGS[shiftKey];
    // Kopiere den Schedule
    const scheduleCopy = {};
    for (let i = 0; i < 7; i++) {
      scheduleCopy[i] = config.schedule?.[i] ? { ...config.schedule[i] } : null;
    }
    setEditSchedule(scheduleCopy);
    setEditingShift(shiftKey);
  };

  // Schichteinstellungen speichern
  const saveShiftConfig = async () => {
    if (!editingShift) return;

    setShiftSaving(true);
    try {
      const newConfig = {
        label: shiftSettings[editingShift]?.label || editingShift,
        schedule: editSchedule
      };
      const { error } = await updateShiftSettings(editingShift, newConfig);
      if (error) {
        setError("Fehler beim Speichern der Schichteinstellungen.");
      } else {
        // Aktualisiere lokalen State
        setShiftSettings(prev => ({
          ...prev,
          [editingShift]: newConfig
        }));
        setEditingShift(null);
        setShiftSaveSuccess(true);
        setTimeout(() => setShiftSaveSuccess(false), 2000);
      }
    } catch (err) {
      setError("Fehler beim Speichern der Schichteinstellungen.");
    } finally {
      setShiftSaving(false);
    }
  };

  // Tag aktivieren/deaktivieren
  const toggleDay = (day) => {
    setEditSchedule(prev => ({
      ...prev,
      [day]: prev[day] ? null : { start: "09:00", end: "18:00" }
    }));
  };

  // Zeit für einen Tag ändern
  const updateDayTime = (day, field, value) => {
    setEditSchedule(prev => ({
      ...prev,
      [day]: prev[day] ? { ...prev[day], [field]: value } : { start: "09:00", end: "18:00", [field]: value }
    }));
  };

  // Alle verfügbaren Zeiten generieren
  const timeOptions = [];
  for (let h = 6; h <= 23; h++) {
    timeOptions.push(`${h.toString().padStart(2, "0")}:00`);
    timeOptions.push(`${h.toString().padStart(2, "0")}:30`);
  }

  // Schicht auswählen - zeigt Datum-Picker
  const handleShiftSelect = (newShift) => {
    // Zeige Datum-Picker (auch wenn bereits aktiv - zum Planen für anderen Zeitraum)
    setShowDatePicker(newShift);
    // Standard: morgen (für Planung)
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setSelectedStartDate(tomorrow.toISOString().split('T')[0]);
    setSelectedEndDate("");
  };

  // Schicht zum Schichtplan hinzufügen
  const handleShiftChange = async () => {
    if (!showDatePicker) return;

    setShiftSaving(true);
    setShiftSaveSuccess(false);
    try {
      const today = new Date().toISOString().split('T')[0];

      // Wenn Startdatum heute oder früher: Als aktuelle Schicht setzen
      if (selectedStartDate <= today && !selectedEndDate) {
        const { error } = await setShiftSetting(showDatePicker, selectedStartDate);
        if (error) {
          setError("Fehler beim Speichern der Schichteinstellung.");
        } else {
          setCurrentShift(showDatePicker);
          setShiftSaveSuccess(true);
          setTimeout(() => setShiftSaveSuccess(false), 2000);
        }
      } else {
        // Als geplante Schicht hinzufügen
        const { plannedShifts: updated, error } = await addPlannedShift(
          showDatePicker,
          selectedStartDate,
          selectedEndDate || null
        );

        if (error) {
          setError(error);
        } else {
          setPlannedShifts(updated);
          setShiftSaveSuccess(true);
          setTimeout(() => setShiftSaveSuccess(false), 2000);
        }
      }
    } catch (err) {
      setError("Fehler beim Speichern der Schichteinstellung.");
    } finally {
      setShiftSaving(false);
      setShowDatePicker(null);
      setSelectedEndDate("");
    }
  };

  // Tage-Namen für Anzeige
  const dayNames = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
  const dayNamesFull = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];

  // Schichtbeschreibung dynamisch generieren
  const getShiftDescription = (shiftKey) => {
    const config = shiftSettings[shiftKey];
    if (!config || !config.schedule) {
      return "Nicht konfiguriert";
    }

    const activeDays = Object.entries(config.schedule)
      .filter(([_, schedule]) => schedule !== null)
      .map(([day, _]) => dayNames[parseInt(day)]);

    if (activeDays.length === 0) {
      return "Alle Termine blockiert";
    }

    return activeDays.join(", ");
  };

  useEffect(() => {
    if (editMode && editingAppointment) {
      setFormData({
        time: editingAppointment.time,
        service: editingAppointment.service,
        date: new Date(editingAppointment.date).toISOString().split("T")[0],
      });
      // Verwende das korrekte Datum für die Zeitfilterung
      const appointmentDate = new Date(editingAppointment.date).toISOString().split("T")[0];
      filterAvailableTimes(appointmentDate, editingAppointment.id);
    }
  }, [editMode, editingAppointment]);

  const fetchAppointmentsFromDB = async () => {
    try {
      const data = await fetchAppointments();

      // Mapping für Wochentage
      const weekdays = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

      // Sortiere die Termine nach Datum und Zeit
      const sortedAppointments = (data || [])
        .map((appointment) => {
          const dateObj = new Date(appointment.date);
          const weekday = weekdays[dateObj.getDay()]; // Wochentag aus Datum extrahieren
          return {
            ...appointment,
            weekday, // Füge Wochentag hinzu
          };
        })
        .sort((a, b) => {
          const dateA = new Date(a.date).getTime();
          const dateB = new Date(b.date).getTime();
          if (dateA === dateB) {
            return a.time.localeCompare(b.time); // Sortiere nach Zeit, wenn Datum gleich ist
          }
          return dateA - dateB;
        });

      setAppointments(sortedAppointments);
    } catch (err) {
      console.error("Fehler beim Abrufen der Termine:", err);
      setError("Fehler beim Laden der Termine.");
    }
  };

  // Hilfsfunktionen für lokales Datum ohne Zeitzonenverschiebung
  function formatLocalYYYYMMDD(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  function parseLocalYYYYMMDD(value) {
    const [y, m, d] = value.split("-").map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
  }

  const fetchBlockedDates = async () => {
    try {
      const { data, error } = await supabase.from("blocked_dates").select("date");
      if (error) throw error;
      // Stelle sicher, dass die gespeicherten Datumswerte als "lokales" Datum interpretiert werden
      setBlockedDates(
        data.map((entry) => {
          const [y, m, d] = String(entry.date).split("-").map(Number);
          return new Date(y, (m || 1) - 1, d || 1);
        })
      );
    } catch (err) {
      setError("Fehler beim Abrufen der gesperrten Tage.");
    }
  };

  const generateAvailableDates = async () => {
    try {
      const today = new Date();
      const next30Days = []; // Erweitert auf 30 Tage für mehr Flexibilität
      for (let i = 0; i < 30; i++) {
        const date = new Date();
        date.setDate(today.getDate() + i);
        if (isWeekday(date)) {
          next30Days.push(date);
        }
      }

      setAvailableDates(next30Days);
    } catch (err) {
      setError("Fehler beim Laden der verfügbaren Tage.");
    }
  };

  // Hilfsfunktion: Gesamtdauer aller gewählten Dienstleistungen
  const getTotalDuration = (servicesArr) => {
    if (!servicesArr || servicesArr.length === 0) return 0;
    let total = 0;
    servicesArr.forEach(service => {
      if (service === "Haare und Bart") total += 45;
      else if (service === "Bart" || service === "Augenbrauen zupfen") total += 15;
      else total += 30; // Haare
    });
    return total;
  };

  // Hilfsfunktion: Zeit-String in HH:mm normalisieren
  const normalizeTime = (t) => {
    if (!t) return "";
    if (t.includes("T")) t = t.split("T")[1];
    t = t.trim().split(".")[0];
    const [h, m] = t.split(":");
    return `${(h || "00").padStart(2, "0")}:${(m || "00").padStart(2, "0")}`;
  };

  const filterAvailableTimes = async (date, excludeAppointmentId = null) => {
    try {
      // Hole alle Termine für das gewählte Datum
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      const nextDay = new Date(startOfDay);
      nextDay.setDate(startOfDay.getDate() + 1);

      const { data: appointments, error } = await supabase
        .from("appointments")
        .select("time, service, name, id")
        .gte("date", startOfDay.toISOString())
        .lt("date", nextDay.toISOString())
        .neq("id", excludeAppointmentId);

      if (error) {
        setError("Fehler beim Abrufen der verfügbaren Zeiten.");
        return;
      }

      const allSlots = generateTimeSlots();
      const bookedSlots = new Set();
      const bookedDetails = new Map();

      // Verarbeite jeden Termin und blockiere die entsprechenden Slots
      appointments.forEach(apt => {
        const startTime = normalizeTime(apt.time);

        // Berechne Dauer basierend auf Service
        let duration = 30; // Standard
        if (apt.service) {
          const services = apt.service.split(", ");
          duration = getTotalDuration(services);
        }

        const startIndex = allSlots.indexOf(startTime);

        if (startIndex !== -1) {
          const slotsToBlock = Math.ceil(duration / 30);
          for (let i = 0; i < slotsToBlock; i++) {
            if (startIndex + i < allSlots.length) {
              const slotToBlock = allSlots[startIndex + i];
              bookedSlots.add(slotToBlock);
              // Speichere Details für den ersten Slot des Termins
              if (i === 0) {
                bookedDetails.set(slotToBlock, {
                  name: apt.name,
                  service: apt.service,
                  startTime: startTime,
                  duration: duration
                });
              }
            }
          }
        }
      });

      // Filtere freie und belegte Slots
      const freeTimes = allSlots.filter(slot => !bookedSlots.has(slot));
      const bookedTimesList = allSlots.filter(slot => bookedSlots.has(slot));


      setAvailableTimes(freeTimes);
      setBookedTimes(bookedTimesList);
      setBookedTimeDetails(bookedDetails);
    } catch (err) {
      console.error("Fehler beim Filtern der verfügbaren Zeiten:", err);
      setError("Fehler beim Laden der verfügbaren Zeiten.");
    }
  };

  const isWeekday = (date) => {
    const day = date.getDay();
    return day !== 0 && day !== 6;
  };

  const handleEdit = (appointment) => {
    setEditMode(true);
    setEditingAppointment(appointment);
    // Verwende das korrekte Datum für die Zeitfilterung
    const appointmentDate = new Date(appointment.date).toISOString().split("T")[0];
    filterAvailableTimes(appointmentDate, appointment.id);
  };

  const handleBlockDate = async () => {
    try {
      // Zeitzonen-Korrektur, um nur das Datum zu erhalten
      const localDate = new Date(
        selectedDate.getTime() - selectedDate.getTimezoneOffset() * 60000
      );
      const dateStr = localDate.toISOString().split("T")[0]; // Nur Datum (YYYY-MM-DD)

      // Überprüfen, ob das Datum bereits gesperrt ist
      const { data: existing, error: fetchError } = await supabase
        .from("blocked_dates")
        .select("date")
        .eq("date", dateStr);

      if (fetchError) {
        console.error("Fehler beim Abrufen gesperrter Tage:", fetchError);
        setError("Fehler beim Abrufen bestehender gesperrter Tage.");
        return;
      }

      if (existing.length > 0) {
        setError("Dieser Tag ist bereits gesperrt.");
        return;
      }

      // Neues Datum hinzufügen
      const { error } = await supabase.from("blocked_dates").insert([{ date: dateStr }]);
      if (error) {
        console.error("Fehler beim Sperren des Tages:", error);
        setError("Fehler beim Sperren des Tages.");
        return;
      }

      // Gesperrten Tag lokal hinzufügen
      setBlockedDates((prev) => [...prev, localDate]);
      setError("");
      console.log("Tag erfolgreich gesperrt:", dateStr);
    } catch (err) {
      console.error("Allgemeiner Fehler beim Sperren des Tages:", err);
      setError("Allgemeiner Fehler beim Sperren des Tages.");
    }
  };

  const handleUnblockDate = async (dateToUnblock) => {
    try {
      // Zeitzonen-Korrektur, um nur das richtige Datum zu erhalten
      const localDate = new Date(
        dateToUnblock.getTime() - dateToUnblock.getTimezoneOffset() * 60000
      );
      const dateStr = localDate.toISOString().split("T")[0]; // Nur Datum (YYYY-MM-DD)

      // Entfernen des gesperrten Datums aus der Datenbank
      const { error } = await supabase.from("blocked_dates").delete().eq("date", dateStr);

      if (error) {
        console.error("Fehler beim Entsperren des Tages:", error);
        setError("Fehler beim Entsperren des Tages.");
        return;
      }

      // Entfernen des Datums aus dem lokalen State
      setBlockedDates((prev) =>
        prev.filter((date) => {
          const localPrevDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
          return localPrevDate.toISOString().split("T")[0] !== dateStr;
        })
      );

      setError("");
      console.log("Tag erfolgreich entsperrt:", dateStr);
    } catch (err) {
      console.error("Allgemeiner Fehler beim Entsperren des Tages:", err);
      setError("Allgemeiner Fehler beim Entsperren des Tages.");
    }
  };

  const handleClearAllBlockedDates = async () => {
    if (blockedDates.length === 0) {
      setError("Keine gesperrten Tage vorhanden.");
      return;
    }

    if (!window.confirm("Möchten Sie wirklich ALLE gesperrten Tage aufheben? Diese Aktion kann nicht rückgängig gemacht werden.")) {
      return;
    }

    try {
      // Alle gesperrten Tage aus der Datenbank löschen
      const { error } = await supabase.from("blocked_dates").delete().neq("id", 0);

      if (error) {
        console.error("Fehler beim Löschen aller gesperrten Tage:", error);
        setError("Fehler beim Löschen aller gesperrten Tage.");
        return;
      }

      // Lokalen State leeren
      setBlockedDates([]);
      setError("");
      console.log("Alle gesperrten Tage erfolgreich gelöscht.");
    } catch (err) {
      console.error("Allgemeiner Fehler beim Löschen aller gesperrten Tage:", err);
      setError("Allgemeiner Fehler beim Löschen aller gesperrten Tage.");
    }
  };


  const handleSave = async () => {
    try {
      if (!editingAppointment) return;

      const oldAppointment = { ...editingAppointment };
      const updates = {
        time: formData.time,
        service: formData.service,
        date: new Date(formData.date).toISOString(),
      };

      const { data, error } = await updateAppointment(editingAppointment.id, updates);

      if (error) {
        if (error.message.includes("unique_date_time_per_appointment")) {
          setError("Die gewählte Zeit ist bereits gebucht. Bitte wählen Sie eine andere Zeit.");
        } else {
          throw new Error(error.message);
        }
        return;
      }

      setAppointments((prev) =>
        prev.map((appt) =>
          appt.id === editingAppointment.id ? { ...appt, ...updates } : appt
        )
      );

      setEditMode(false);
      setEditingAppointment(null);
      setFormData({ time: "", service: "", date: "" });
      setError("");
    } catch (err) {
      setError("Fehler beim Speichern der Änderungen.");
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setEditingAppointment(null);
    setFormData({ time: "", service: "", date: "" });
    setAvailableTimes([]);
    setBookedTimes([]);
    setBookedTimeDetails(new Map());
  };

  const handleDelete = async (appointmentId) => {
    try {
      // Zeige dem Nutzer, dass der Löschvorgang läuft
      setError(""); // Reset vorherige Fehler
      setAppointments((prev) =>
        prev.map((appt) =>
          appt.id === appointmentId ? { ...appt, deleting: true } : appt
        )
      );

      // Finde den Termin für die E-Mail-Benachrichtigung
      const appointmentToDelete = appointments.find(appt => appt.id === appointmentId);

      // Versuche, den Termin zu löschen
      const { error } = await deleteAppointment(appointmentId);

      if (error) {
        throw new Error(error.message);
      }

      // Entferne den Termin aus der Liste, wenn erfolgreich
      setAppointments((prev) => prev.filter((appt) => appt.id !== appointmentId));
    } catch (err) {
      setError("Fehler beim Löschen des Termins. Bitte versuchen Sie es erneut.");
      console.error("Löschfehler:", err.message);
    } finally {
      // Entferne den "deleting"-Status
      setAppointments((prev) =>
        prev.map((appt) =>
          appt.id === appointmentId ? { ...appt, deleting: false } : appt
        )
      );
    }
  };


  const handleRefresh = async () => {
    if (isRefreshing) return; // Verhindere mehrfaches Klicken

    setError("");
    setRefreshSuccess(false);
    setIsRefreshing(true);

    try {
      await Promise.all([
        fetchAppointmentsFromDB(),
        fetchBlockedDates(),
        generateAvailableDates()
      ]);
      console.log("✅ Admin Panel refreshed successfully");

      // Zeige Erfolgsmeldung
      setRefreshSuccess(true);

      // Verstecke Erfolgsmeldung nach 2 Sekunden
      setTimeout(() => {
        setRefreshSuccess(false);
      }, 2000);
    } catch (err) {
      console.error("❌ Error refreshing admin panel:", err);
      setError("Fehler beim Aktualisieren der Daten.");
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("isAdmin");
    onLogout();
    navigate("/login");
  };

  if (!userRole) {
    return <p>Lade Admin-Berechtigungen...</p>;
  }


  return (
    <div className="bg-app min-h-screen p-4">
      <div className="max-w-6xl 2xl:max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-zinc-800/90 backdrop-blur-xl border border-gold-500/20 rounded-2xl shadow-2xl p-4 sm:p-6 mb-6 relative animate-fade-in">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <div className="text-center sm:text-left flex-1">
              <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gold-400 to-gold-600 bg-clip-text text-transparent mb-2">Admin Panel</h2>
              <p className="text-zinc-400 text-sm sm:text-base">Terminverwaltung</p>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3 w-full sm:w-auto">
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className={`flex-1 sm:flex-none bg-zinc-700/80 hover:bg-zinc-600/80 border border-zinc-600/50 hover:border-gold-500/40 text-zinc-100 font-medium py-2 sm:py-3 px-3 sm:px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-1 sm:space-x-2 ${
                  isRefreshing ? 'opacity-75 cursor-not-allowed' : ''
                } ${refreshSuccess ? 'bg-green-600 hover:bg-green-500' : ''}`}
                title={isRefreshing ? "Daten werden aktualisiert..." : "Daten aktualisieren"}
              >
                <svg
                  className={`w-4 h-4 sm:w-5 sm:h-5 ${isRefreshing ? 'animate-spin' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  {isRefreshing ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  ) : refreshSuccess ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  )}
                </svg>
                <span className="text-sm sm:text-base">
                  {isRefreshing ? 'Lädt...' : refreshSuccess ? 'Erfolgreich!' : 'Refresh'}
                </span>
              </button>
              <PushNotificationToggle />
              <InstallPWA />
              <button
                onClick={handleLogout}
                className="flex-1 sm:flex-none bg-zinc-700/80 hover:bg-zinc-600/80 border border-zinc-600/50 hover:border-gold-500/40 text-zinc-100 font-medium py-2 sm:py-3 px-3 sm:px-6 rounded-lg transition-all duration-200 flex items-center justify-center space-x-1 sm:space-x-2"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span className="text-sm sm:text-base">Logout</span>
              </button>
            </div>
          </div>
        </div>

        {/* === NOTIFICATIONS ACCORDION === */}
        <div className="mb-6">
          <button
            className="w-full flex items-center justify-between bg-zinc-800/90 backdrop-blur-xl border border-gold-500/20 hover:border-gold-500/40 text-zinc-100 text-base rounded-xl px-5 py-3 font-semibold shadow transition-all duration-200 focus:outline-none"
            onClick={() => { setIsNotificationsOpen((open) => !open); setUnreadCount(0); }}
          >
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-gold-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <span>Benachrichtigungen</span>
              {unreadCount > 0 && (
                <span className="bg-gold-500 text-black text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center animate-scale-in">
                  {unreadCount}
                </span>
              )}
            </div>
            <svg className={`w-5 h-5 ml-2 transition-transform duration-200 ${isNotificationsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
          </button>
          <div
            className={`overflow-hidden transition-all duration-500 ${isNotificationsOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}
            style={{ transitionProperty: 'max-height, opacity' }}
          >
            {isNotificationsOpen && (
              <div className="bg-zinc-800/90 backdrop-blur-xl border border-gold-500/20 rounded-2xl shadow-2xl mt-3 overflow-hidden">
                {notifications.length === 0 ? (
                  <div className="p-8 text-center text-zinc-500 text-sm">
                    <svg className="w-10 h-10 mx-auto mb-3 text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    Noch keine Benachrichtigungen. Sobald ein Kunde einen Termin bucht, aendert oder loescht, erscheint es hier live.
                  </div>
                ) : (
                  <div className="max-h-[400px] overflow-y-auto divide-y divide-zinc-700/50">
                    {notifications.map((n) => (
                      <div key={n.id} className={`flex items-start gap-3 p-4 transition-colors duration-200 ${!n.read ? 'bg-gold-500/5' : 'hover:bg-zinc-700/30'}`}>
                        <div className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                          n.type === 'new' ? 'bg-emerald-500/20 text-emerald-400' :
                          n.type === 'update' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {n.type === 'new' && (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                          )}
                          {n.type === 'update' && (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                          )}
                          {n.type === 'delete' && (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className={`text-sm font-semibold ${
                              n.type === 'new' ? 'text-emerald-400' :
                              n.type === 'update' ? 'text-blue-400' :
                              'text-red-400'
                            }`}>{n.title}</span>
                            <span className="text-xs text-zinc-500 shrink-0 ml-2">{n.time}</span>
                          </div>
                          <p className="text-sm text-zinc-300 truncate mt-0.5">{n.detail}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {notifications.length > 0 && (
                  <div className="p-3 border-t border-zinc-700/50 flex justify-end">
                    <button
                      onClick={() => { setNotifications([]); setUnreadCount(0); }}
                      className="text-xs text-zinc-500 hover:text-gold-400 transition-all duration-200 px-3 py-1.5 rounded-lg hover:bg-zinc-700/50"
                    >
                      Alle loeschen
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-900/20 border border-red-500/30 rounded-lg flex items-start space-x-3">
            <svg className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-red-300 text-sm">{error}</p>
          </div>
        )}
        {/* Admin-Termin-Erstellung als Accordion */}
        <div className="mb-6">
          <button
            className="w-full flex items-center justify-between bg-zinc-800/90 backdrop-blur-xl border border-gold-500/20 hover:border-gold-500/40 text-zinc-100 text-base rounded-xl px-5 py-3 font-semibold shadow transition-all duration-200 focus:outline-none"
            onClick={() => setIsCreateOpen((open) => !open)}
          >
            <span>Termin für Nutzer anlegen</span>
            <svg className={`w-5 h-5 ml-2 transition-transform duration-200 ${isCreateOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
          </button>

          <button
            className="w-full mt-3 flex items-center justify-center bg-blue-600 border border-blue-500 text-white text-base rounded-xl px-5 py-3 font-semibold shadow hover:bg-blue-700 hover:border-blue-400 transition-all duration-200 focus:outline-none"
            onClick={() => navigate('/reminder-test')}
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            <span>Erinnerungs-E-Mails testen</span>
          </button>
          <div
            className={`overflow-hidden transition-all duration-500 ${isCreateOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}
            style={{ transitionProperty: 'max-height, opacity' }}
          >
            {isCreateOpen && (
              <div className="mt-4">
                <AdminCreateAppointment />
              </div>
            )}
          </div>
        </div>

        {/* Schichtsystem Accordion */}
        <div className="mb-6">
          <button
            className="w-full flex items-center justify-between bg-gradient-to-r from-amber-600 to-orange-600 border border-amber-500 text-white text-sm sm:text-base rounded-xl px-3 sm:px-5 py-3 font-semibold shadow hover:from-amber-700 hover:to-orange-700 transition-all duration-200 focus:outline-none"
            onClick={() => setIsShiftOpen((open) => !open)}
          >
            <div className="flex items-center space-x-1.5 sm:space-x-2 min-w-0">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="truncate">Schichtsystem</span>
              <span className="ml-1 sm:ml-2 px-1.5 sm:px-2 py-0.5 bg-white/20 rounded-full text-xs whitespace-nowrap">
                {currentShift === "normal" ? "Normal" :
                 currentShift === "fruehschicht" ? "Früh" :
                 currentShift === "spaetschicht" ? "Spät" : "Nacht"}
              </span>
            </div>
            <svg className={`w-4 h-4 sm:w-5 sm:h-5 ml-2 flex-shrink-0 transition-transform duration-200 ${isShiftOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
          </button>
          <div
            className={`overflow-hidden transition-all duration-500 ${isShiftOpen ? 'max-h-[1200px] opacity-100' : 'max-h-0 opacity-0'}`}
            style={{ transitionProperty: 'max-height, opacity' }}
          >
            {isShiftOpen && (
              <div className="bg-zinc-800/90 backdrop-blur-xl border border-gold-500/20 rounded-2xl shadow-2xl p-4 mt-4">
                {/* Aktuelle Schicht Anzeige */}
                <div className="mb-4 p-3 bg-black/30 border border-zinc-600 rounded-xl">
                  <p className="text-zinc-400 text-xs mb-1">Aktiv:</p>
                  <p className="text-zinc-100 font-semibold mb-2">
                    {shiftSettings[currentShift]?.label || currentShift}
                  </p>
                  {shiftSettings[currentShift]?.schedule && (
                    <div className="text-xs text-zinc-400 space-y-0.5">
                      {Object.entries(shiftSettings[currentShift].schedule)
                        .filter(([_, schedule]) => schedule !== null)
                        .map(([day, schedule]) => (
                          <div key={day} className="flex justify-between">
                            <span>{dayNames[parseInt(day)]}</span>
                            <span className="text-zinc-300">{schedule.start} - {schedule.end}</span>
                          </div>
                        ))}
                      {Object.values(shiftSettings[currentShift].schedule).every(s => s === null) && (
                        <span className="text-red-400">Alle Tage blockiert</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Schichtplan Anzeige */}
                {plannedShifts.length > 0 && (
                  <div className="mb-4 space-y-2">
                    <p className="text-zinc-400 text-xs font-medium mb-2 flex items-center gap-1.5">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                      Geplante Schichten:
                    </p>
                    {plannedShifts.map((ps, index) => (
                      <div key={index} className="p-3 bg-amber-900/20 border border-amber-500/50 rounded-xl">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-amber-300 font-semibold truncate">
                              {shiftSettings[ps.shift]?.label || ps.shift}
                            </p>
                            <p className="text-amber-400 text-xs">
                              {new Date(ps.startDate).toLocaleDateString('de-DE')}
                              {ps.endDate && ` - ${new Date(ps.endDate).toLocaleDateString('de-DE')}`}
                            </p>
                          </div>
                          <button
                            onClick={async () => {
                              const { error } = await removePlannedShift(index);
                              if (!error) {
                                setPlannedShifts(prev => prev.filter((_, i) => i !== index));
                              } else {
                                alert("Fehler: " + error);
                              }
                            }}
                            className="text-amber-400 hover:text-amber-300 p-2 ml-2 flex-shrink-0"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Schichtauswahl als Liste */}
                <div className="space-y-2">
                  {/* Normal */}
                  <div className={`rounded-xl border-2 p-3 transition-all ${currentShift === "normal" ? "bg-green-900/30 border-green-500" : plannedShifts.some(ps => ps.shift === "normal") ? "bg-green-900/10 border-green-500/30" : "bg-zinc-900 border-zinc-700"}`}>
                    <div className="flex items-center justify-between">
                      <button onClick={() => handleShiftSelect("normal")} disabled={shiftSaving} className="flex items-center gap-3 flex-1 text-left">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${currentShift === "normal" ? "bg-green-600" : "bg-zinc-700"}`}>
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className={`font-semibold ${currentShift === "normal" ? "text-green-300" : "text-zinc-100"}`}>Normal</p>
                          <p className="text-zinc-400 text-xs truncate">{getShiftDescription("normal")}</p>
                        </div>
                      </button>
                      <button onClick={() => openEditShift("normal")} className="ml-2 p-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg">
                        <svg className="w-5 h-5 text-zinc-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                      </button>
                    </div>
                  </div>

                  {/* Frühschicht */}
                  <div className={`rounded-xl border-2 p-3 transition-all ${currentShift === "fruehschicht" ? "bg-amber-900/30 border-amber-500" : plannedShifts.some(ps => ps.shift === "fruehschicht") ? "bg-amber-900/10 border-amber-500/30" : "bg-zinc-900 border-zinc-700"}`}>
                    <div className="flex items-center justify-between">
                      <button onClick={() => handleShiftSelect("fruehschicht")} disabled={shiftSaving} className="flex items-center gap-3 flex-1 text-left">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${currentShift === "fruehschicht" ? "bg-amber-600" : "bg-zinc-700"}`}>
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className={`font-semibold ${currentShift === "fruehschicht" ? "text-amber-300" : "text-zinc-100"}`}>Frühschicht</p>
                          <p className="text-zinc-400 text-xs truncate">{getShiftDescription("fruehschicht")}</p>
                        </div>
                      </button>
                      <button onClick={() => openEditShift("fruehschicht")} className="ml-2 p-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg">
                        <svg className="w-5 h-5 text-zinc-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                      </button>
                    </div>
                  </div>

                  {/* Spätschicht */}
                  <div className={`rounded-xl border-2 p-3 transition-all ${currentShift === "spaetschicht" ? "bg-blue-900/30 border-blue-500" : plannedShifts.some(ps => ps.shift === "spaetschicht") ? "bg-blue-900/10 border-blue-500/30" : "bg-zinc-900 border-zinc-700"}`}>
                    <div className="flex items-center justify-between">
                      <button onClick={() => handleShiftSelect("spaetschicht")} disabled={shiftSaving} className="flex items-center gap-3 flex-1 text-left">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${currentShift === "spaetschicht" ? "bg-blue-600" : "bg-zinc-700"}`}>
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className={`font-semibold ${currentShift === "spaetschicht" ? "text-blue-300" : "text-zinc-100"}`}>Spätschicht</p>
                          <p className="text-zinc-400 text-xs truncate">{getShiftDescription("spaetschicht")}</p>
                        </div>
                      </button>
                      <button onClick={() => openEditShift("spaetschicht")} className="ml-2 p-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg">
                        <svg className="w-5 h-5 text-zinc-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                      </button>
                    </div>
                  </div>

                  {/* Nachtschicht */}
                  <div className={`rounded-xl border-2 p-3 transition-all ${currentShift === "nachtschicht" ? "bg-red-900/30 border-red-500" : plannedShifts.some(ps => ps.shift === "nachtschicht") ? "bg-red-900/10 border-red-500/30" : "bg-zinc-900 border-zinc-700"}`}>
                    <div className="flex items-center justify-between">
                      <button onClick={() => handleShiftSelect("nachtschicht")} disabled={shiftSaving} className="flex items-center gap-3 flex-1 text-left">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${currentShift === "nachtschicht" ? "bg-red-600" : "bg-zinc-700"}`}>
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className={`font-semibold ${currentShift === "nachtschicht" ? "text-red-300" : "text-zinc-100"}`}>Nachtschicht</p>
                          <p className="text-zinc-400 text-xs truncate">Blockiert</p>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Status */}
                {shiftSaving && (
                  <div className="mt-4 p-3 bg-black/30 border border-zinc-600 rounded-lg flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-zinc-400" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-zinc-400">Speichere...</span>
                  </div>
                )}
                {shiftSaveSuccess && (
                  <div className="mt-4 p-3 bg-green-900/20 border border-green-500/30 rounded-lg flex items-center justify-center gap-2">
                    <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-green-300">Gespeichert!</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Modal: Datum wählen für Schichtwechsel - Mit Start & Ende */}
        {showDatePicker && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4" onClick={() => { setShowDatePicker(null); setSelectedEndDate(""); }}>
            <div className="bg-zinc-900/95 backdrop-blur-xl border border-gold-500/20 w-full max-w-sm rounded-xl shadow-2xl p-5 animate-scale-in" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-semibold text-zinc-100 mb-2 text-center text-lg">
                {shiftSettings[showDatePicker]?.label || showDatePicker}
              </h3>
              <p className="text-zinc-400 text-sm mb-4 text-center">Zeitraum für diese Schicht festlegen</p>

              {/* Startdatum */}
              <div className="mb-3">
                <label className="text-zinc-400 text-xs block mb-1">Startdatum *</label>
                <input
                  type="date"
                  value={selectedStartDate}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setSelectedStartDate(e.target.value)}
                  className="w-full bg-black/30 border border-zinc-700/50 focus:ring-gold-500/20 focus:border-gold-500/50 rounded-xl px-4 py-3 text-zinc-100 text-base appearance-none"
                  style={{ colorScheme: 'dark' }}
                />
              </div>

              {/* Enddatum (optional) */}
              <div className="mb-5">
                <label className="text-zinc-400 text-xs block mb-1">Enddatum (optional)</label>
                <input
                  type="date"
                  value={selectedEndDate}
                  min={selectedStartDate || new Date().toISOString().split('T')[0]}
                  onChange={(e) => setSelectedEndDate(e.target.value)}
                  className="w-full bg-black/30 border border-zinc-700/50 focus:ring-gold-500/20 focus:border-gold-500/50 rounded-xl px-4 py-3 text-zinc-100 text-base appearance-none"
                  style={{ colorScheme: 'dark' }}
                />
                <p className="text-zinc-500 text-xs mt-1">Ohne Enddatum gilt die Schicht unbegrenzt</p>
              </div>

              <div className="flex gap-3">
                <button onClick={() => { setShowDatePicker(null); setSelectedEndDate(""); }} className="flex-1 bg-zinc-700/80 hover:bg-zinc-600/80 border border-zinc-600/50 hover:border-gold-500/40 active:bg-zinc-500 text-zinc-100 font-medium py-3.5 rounded-xl text-base">
                  Abbrechen
                </button>
                <button onClick={handleShiftChange} disabled={shiftSaving || !selectedStartDate} className="flex-1 bg-gradient-to-r from-gold-600 to-gold-700 hover:from-gold-500 hover:to-gold-600 active:from-gold-700 active:to-gold-800 text-white font-medium py-3.5 rounded-xl text-base disabled:opacity-50">
                  {shiftSaving ? "..." : "Hinzufügen"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Schicht bearbeiten - KOMPAKT */}
        {editingShift && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4" onClick={() => setEditingShift(null)}>
            <div className="bg-zinc-900/95 backdrop-blur-xl border border-gold-500/20 w-full max-w-sm rounded-xl shadow-2xl flex flex-col max-h-[80vh] animate-scale-in" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="px-4 py-3 border-b border-gold-500/20 flex items-center justify-between">
                <span className="font-semibold text-zinc-100">{shiftSettings[editingShift]?.label}</span>
                <button onClick={() => setEditingShift(null)} className="text-zinc-400 hover:text-zinc-200">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>

              {/* Content */}
              <div className="p-3 overflow-y-auto flex-1">
                {[0, 1, 2, 3, 4, 5, 6].map((day) => {
                  const isActive = editSchedule[day] !== null && editSchedule[day] !== undefined;
                  return (
                    <div key={day} className={`flex items-center gap-2 py-2 ${day < 6 ? "border-b border-white/5" : ""}`}>
                      {/* Checkbox */}
                      <button onClick={() => toggleDay(day)} className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${isActive ? "bg-amber-600" : "bg-zinc-700"}`}>
                        {isActive && <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                      </button>

                      {/* Tag */}
                      <span className={`w-12 text-sm ${isActive ? "text-zinc-100" : "text-zinc-500"}`}>{dayNames[day]}</span>

                      {/* Zeit Inputs */}
                      {isActive ? (
                        <div className="flex items-center gap-1 flex-1">
                          <input
                            type="time"
                            value={editSchedule[day]?.start || "09:00"}
                            onChange={(e) => updateDayTime(day, "start", e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="flex-1 bg-black/30 border border-zinc-700/50 focus:ring-gold-500/20 focus:border-gold-500/50 rounded px-2 py-1.5 text-zinc-100 text-sm"
                          />
                          <span className="text-zinc-500">-</span>
                          <input
                            type="time"
                            value={editSchedule[day]?.end || "18:00"}
                            onChange={(e) => updateDayTime(day, "end", e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="flex-1 bg-black/30 border border-zinc-700/50 focus:ring-gold-500/20 focus:border-gold-500/50 rounded px-2 py-1.5 text-zinc-100 text-sm"
                          />
                        </div>
                      ) : (
                        <span className="text-zinc-600 text-sm">—</span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="px-3 py-3 border-t border-gold-500/20 flex gap-2">
                <button onClick={() => setEditingShift(null)} className="flex-1 bg-zinc-700/80 hover:bg-zinc-600/80 border border-zinc-600/50 hover:border-gold-500/40 text-zinc-100 font-medium py-2.5 rounded-lg text-sm">
                  Abbrechen
                </button>
                <button onClick={saveShiftConfig} disabled={shiftSaving} className="flex-1 bg-gradient-to-r from-gold-600 to-gold-700 hover:from-gold-500 hover:to-gold-600 text-white font-medium py-2.5 rounded-lg text-sm flex items-center justify-center gap-1.5">
                  {shiftSaving ? <><svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>...</> : "Speichern"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bereich: Gesperrte Tage verwalten als Accordion */}
        <div className="mb-6">
          <button
            className="w-full flex items-center justify-between bg-zinc-800/90 backdrop-blur-xl border border-gold-500/20 hover:border-gold-500/40 text-zinc-100 text-base rounded-xl px-5 py-3 font-semibold shadow transition-all duration-200 focus:outline-none"
            onClick={() => setIsBlockedOpen((open) => !open)}
          >
            <span>Gesperrte Tage verwalten</span>
            <svg className={`w-5 h-5 ml-2 transition-transform duration-200 ${isBlockedOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
          </button>
          <div
            className={`overflow-hidden transition-all duration-500 ${isBlockedOpen ? 'max-h-[1200px] opacity-100' : 'max-h-0 opacity-0'}`}
            style={{ transitionProperty: 'max-height, opacity' }}
          >
            {isBlockedOpen && (
              <div className="bg-zinc-800/90 backdrop-blur-xl border border-gold-500/20 rounded-2xl shadow-2xl p-6 mt-4">
                <h3 className="text-xl font-semibold text-zinc-100 mb-4 text-center">Gesperrte Tage verwalten</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Datum wählen</label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={selectedDate.toLocaleDateString("de-DE", {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                        readOnly
                        className="flex-1 bg-black/30 border border-zinc-700/50 focus:ring-gold-500/20 focus:border-gold-500/50 rounded-lg px-4 py-3 text-zinc-100 cursor-pointer"
                        onClick={() => setCalendarModalOpen(true)}
                      />
                      <button
                        onClick={() => setCalendarModalOpen(true)}
                        className="bg-zinc-700/80 hover:bg-zinc-600/80 border border-zinc-600/50 hover:border-gold-500/40 text-zinc-100 px-4 py-3 rounded-lg transition-all duration-200 flex items-center justify-center"
                        title="Kalender öffnen"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Blocked Dates List */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-base font-semibold text-zinc-100">Gesperrte Tage</h4>
                      {blockedDates.length > 0 && (
                        <button
                          onClick={handleClearAllBlockedDates}
                          className="bg-red-700 hover:bg-red-800 text-white px-3 py-1 rounded text-sm transition-all duration-200 flex items-center space-x-1"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          <span>Alle löschen</span>
                        </button>
                      )}
                    </div>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {blockedDates.map((date, index) => (
                        <div key={index} className="flex items-center justify-between bg-black/30 border border-red-500/20 rounded-lg p-3">
                          <span className="text-zinc-100">
                            {date.toLocaleDateString("de-DE", {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </span>
                          <button
                            onClick={() => handleUnblockDate(date)}
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-all duration-200"
                          >
                            Entsperren
                          </button>
                        </div>
                      ))}
                      {blockedDates.length === 0 && (
                        <p className="text-zinc-400 text-sm">Keine gesperrten Tage</p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex space-x-3 mt-4">
                  <button
                    onClick={handleBlockDate}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <span>Tag sperren</span>
                  </button>
                  <button
                    onClick={() => handleUnblockDate(selectedDate)}
                    disabled={
                      !blockedDates.some(
                        (blockedDate) =>
                          new Date(blockedDate.getTime() - blockedDate.getTimezoneOffset() * 60000)
                            .toISOString()
                            .split("T")[0] ===
                          new Date(selectedDate.getTime() - selectedDate.getTimezoneOffset() * 60000)
                            .toISOString()
                            .split("T")[0]
                      )
                    }
                    className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-zinc-700 disabled:text-zinc-500 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Tag entsperren</span>
                  </button>
                </div>
                {/* Zeitblock-Sperrung als Grid (mit Entsperren) */}
                <div className="mt-6 p-4 bg-black/30 border border-red-500/10 rounded-xl">
                  <h4 className="text-base font-semibold text-zinc-100 mb-2">Einzelnen Zeitblock sperren</h4>
                  <div className="flex flex-col">
                    <div className="flex flex-wrap gap-2 justify-center max-h-64 overflow-y-auto mb-4">
                      {Array.from({ length: 22 }, (_, i) => {
                        const hour = 9 + Math.floor(i / 2);
                        const minute = i % 2 === 0 ? "00" : "30";
                        return `${hour.toString().padStart(2, "0")}:${minute}`;
                      }).map(slot => {
                        const isBlocked = blockedTimeSlots.includes(slot);
                        const isSelected = slot === selectedTimeBlock && !isBlocked;
                        return (
                          <button
                            key={slot}
                            type="button"
                            disabled={!isBlocked && blockedTimeSlots.includes(slot)}
                            onClick={() => {
                              if (isBlocked) {
                                setUnblockDialog({ open: true, slot });
                              } else {
                                setSelectedTimeBlock(slot);
                              }
                            }}
                            className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-base font-medium transition-all duration-200 flex-shrink-0
                              ${isBlocked ? 'bg-red-900/30 text-red-300 opacity-60 cursor-pointer border border-red-400' :
                                isSelected ? 'bg-gradient-to-r from-gold-600 to-gold-700 text-white font-bold ring-2 ring-gold-400' :
                                  'bg-zinc-800/50 text-zinc-200 hover:border-gold-500/40 hover:bg-zinc-700 hover:text-zinc-100 cursor-pointer border border-zinc-600/50'}
                            `}
                            title={isBlocked ? 'Zum Entsperren klicken' : ''}
                          >
                            {slot}
                            {isBlocked && <span className="ml-1 sm:ml-2 text-xs">belegt</span>}
                            {isSelected && (
                              <span className="ml-1 sm:ml-2 text-zinc-200">
                                <svg className="w-3 h-3 sm:w-4 sm:h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    <div className="sticky bottom-0 bg-black/30 pt-2 pb-2 z-10">
                      <button
                        className="bg-red-600 hover:bg-red-700 disabled:bg-zinc-700 disabled:text-zinc-500 text-white px-3 sm:px-4 py-2 rounded-lg font-semibold transition-all text-sm sm:text-base"
                        disabled={!selectedTimeBlock}
                        onClick={async () => {
                          if (!selectedTimeBlock) return;
                          const selectedDateTime = new Date(selectedDate);
                          const [hours, minutes] = selectedTimeBlock.split(":").map(Number);
                          selectedDateTime.setHours(hours, minutes, 0, 0);
                          await supabase.from("appointments").insert([
                            {
                              user_id: null,
                              name: "BLOCKED",
                              time: selectedTimeBlock,
                              service: "BLOCKED",
                              date: selectedDateTime.toISOString(),
                            },
                          ]);
                          setSelectedTimeBlock("");
                          // Nach dem Sperren: Slots neu laden
                          const startOfDay = new Date(selectedDate);
                          startOfDay.setHours(0, 0, 0, 0);
                          const nextDay = new Date(startOfDay);
                          nextDay.setDate(startOfDay.getDate() + 1);
                          const { data: appointments } = await supabase
                            .from("appointments")
                            .select("time, service")
                            .gte("date", startOfDay.toISOString())
                            .lt("date", nextDay.toISOString());
                          setBlockedTimeSlots((appointments || []).map(a => normalizeTime(a.time)));
                        }}
                      >
                        Zeitblock sperren
                      </button>
                    </div>
                  </div>
                  {/* Dialog zum Entsperren */}
                  {unblockDialog.open && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-2 sm:p-4" onClick={() => setUnblockDialog({ open: false, slot: null })}>
                      <div className="bg-zinc-800 border-2 border-gold-500/30 rounded-2xl shadow-[0_0_60px_rgba(251,191,36,0.12)] max-w-xs sm:max-w-sm w-full mx-2 sm:mx-4 p-0 relative flex flex-col max-h-[90vh] animate-scale-in">
                        <div className="p-4 sm:p-6 overflow-y-auto flex-1">
                          <h3 className="text-base sm:text-lg font-semibold text-zinc-100 mb-3 sm:mb-4 text-center">Zeitblock entsperren</h3>
                          <p className="text-zinc-300 mb-3 sm:mb-4 text-center text-sm sm:text-base">Möchtest du den Zeitblock <b className="text-zinc-100">{unblockDialog.slot}</b> wirklich entsperren?</p>
                        </div>
                        <div className="flex justify-end gap-2 p-3 sm:p-4 border-t border-gold-500/20 bg-black/30 sticky bottom-0 z-10">
                          <button className="px-3 sm:px-4 py-2 rounded bg-zinc-600 text-zinc-100 hover:bg-zinc-700 text-sm sm:text-base" onClick={() => setUnblockDialog({ open: false, slot: null })}>Abbrechen</button>
                          <button
                            className="px-3 sm:px-4 py-2 rounded bg-green-600 text-white font-semibold hover:bg-green-700 text-sm sm:text-base"
                            onClick={async () => {
                              // Lösche BLOCKED-Eintrag für diesen Slot und Tag
                              const selectedDateTime = new Date(selectedDate);
                              const [hours, minutes] = unblockDialog.slot.split(":").map(Number);
                              selectedDateTime.setHours(hours, minutes, 0, 0);
                              await supabase.from("appointments")
                                .delete()
                                .eq("date", selectedDateTime.toISOString())
                                .eq("time", unblockDialog.slot)
                                .eq("service", "BLOCKED");
                              setUnblockDialog({ open: false, slot: null });
                              // Nach dem Entsperren: Slots neu laden
                              const startOfDay = new Date(selectedDate);
                              startOfDay.setHours(0, 0, 0, 0);
                              const nextDay = new Date(startOfDay);
                              nextDay.setDate(startOfDay.getDate() + 1);
                              const { data: appointments } = await supabase
                                .from("appointments")
                                .select("time, service")
                                .gte("date", startOfDay.toISOString())
                                .lt("date", nextDay.toISOString());
                              setBlockedTimeSlots((appointments || []).map(a => normalizeTime(a.time)));
                            }}
                          >
                            Entsperren
                          </button>
                        </div>
                        <button className="absolute top-2 sm:top-3 right-2 sm:right-3 text-zinc-400 hover:text-zinc-200 p-1 transition-all duration-200" onClick={() => setUnblockDialog({ open: false, slot: null })} aria-label="Schließen">
                          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {editMode ? (
          <div className="bg-zinc-800/90 backdrop-blur-xl border border-gold-500/20 rounded-2xl shadow-2xl p-6">
            <h3 className="text-xl font-semibold text-zinc-100 mb-6 text-center">Termin bearbeiten</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Datum</label>
                <select
                  value={formData.date}
                  onChange={(e) => {
                    setFormData({ ...formData, date: e.target.value, time: "" }); // Reset time when date changes
                    filterAvailableTimes(e.target.value, editingAppointment?.id);
                  }}
                  className="w-full bg-black/30 border border-zinc-700/50 rounded-lg px-4 py-3 text-zinc-100 focus:ring-2 focus:ring-gold-500/20 focus:border-gold-500/50"
                >
                  <option value="">Datum wählen</option>
                  {availableDates.map((date) => (
                    <option key={date.toISOString()} value={date.toISOString().split("T")[0]}>
                      {date.toLocaleDateString("de-DE")}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Zeit</label>
                <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto p-2 bg-black/30 border border-zinc-700/50 rounded-lg">
                  {generateTimeSlots().map((time) => {
                    const isBooked = bookedTimes.includes(time);
                    const isSelected = formData.time === time;
                    const bookedInfo = bookedTimeDetails.get(time);

                    return (
                      <button
                        key={time}
                        type="button"
                        onClick={() => {
                          if (!isBooked) {
                            setFormData({ ...formData, time: time });
                          }
                        }}
                        disabled={isBooked}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          isSelected
                            ? 'bg-gradient-to-r from-gold-600 to-gold-700 text-white ring-2 ring-gold-400'
                            : isBooked
                            ? 'bg-red-900/30 text-red-300 border border-red-500 cursor-not-allowed opacity-60'
                            : 'bg-zinc-800/50 text-zinc-200 border border-zinc-600/50 hover:border-gold-500/40 hover:bg-zinc-700'
                        }`}
                        title={
                          isBooked && bookedInfo
                            ? `🚫 BELEGT: ${bookedInfo.name} - ${bookedInfo.service} (${bookedInfo.duration}min)`
                            : isBooked
                            ? '🚫 Belegt'
                            : '✅ Verfügbar'
                        }
                      >
                        <div className="flex flex-col items-center">
                          <span>{time}</span>
                          {isBooked && bookedInfo && (
                            <span className="text-xs text-red-200 truncate max-w-16">
                              {bookedInfo.name}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Legende */}
                <div className="flex flex-wrap justify-center gap-4 text-xs text-zinc-400 mt-2">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-zinc-800/50 border border-zinc-600/50 rounded"></div>
                    <span>Verfügbar</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-red-900/30 border border-red-500 rounded"></div>
                    <span>Belegt</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-gradient-to-r from-gold-600 to-gold-700 rounded"></div>
                    <span>Ausgewählt</span>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Dienstleistung</label>
                <input
                  type="text"
                  placeholder="Dienstleistung"
                  value={formData.service}
                  onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                  className="w-full bg-black/30 border border-zinc-700/50 rounded-lg px-4 py-3 text-zinc-100 placeholder-zinc-500 focus:ring-2 focus:ring-gold-500/20 focus:border-gold-500/50"
                />
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleSave}
                  className="flex-1 bg-gradient-to-r from-gold-600 to-gold-700 hover:from-gold-500 hover:to-gold-600 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Speichern</span>
                </button>
                <button
                  onClick={handleCancel}
                  className="flex-1 bg-zinc-700/80 hover:bg-zinc-600/80 border border-zinc-600/50 hover:border-gold-500/40 text-zinc-100 font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span>Abbrechen</span>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-zinc-800/90 backdrop-blur-xl border border-gold-500/20 rounded-2xl shadow-2xl overflow-hidden animate-slide-up">
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto min-w-0">
              <table className="w-full min-w-max">
                <thead className="bg-black/40">
                  <tr>
                    <th className="px-3 py-4 text-left text-xs font-medium text-gold-400 uppercase tracking-wider whitespace-nowrap">Name</th>
                    <th className="px-3 py-4 text-left text-xs font-medium text-gold-400 uppercase tracking-wider whitespace-nowrap">Datum</th>
                    <th className="px-3 py-4 text-left text-xs font-medium text-gold-400 uppercase tracking-wider whitespace-nowrap">Wochentag</th>
                    <th className="px-3 py-4 text-left text-xs font-medium text-gold-400 uppercase tracking-wider whitespace-nowrap">Zeit</th>
                    <th className="px-3 py-4 text-left text-xs font-medium text-gold-400 uppercase tracking-wider whitespace-nowrap">Dienstleistung</th>
                    <th className="px-3 py-4 text-left text-xs font-medium text-gold-400 uppercase tracking-wider whitespace-nowrap">Aktionen</th>
                  </tr>
                </thead>
            <tbody>
              {/* Termine nach Datum gruppieren */}
              {Object.entries(
                appointments.reduce((acc, appt) => {
                  const dateKey = new Date(appt.date).toLocaleDateString("de-DE");
                  if (!acc[dateKey]) acc[dateKey] = [];
                  acc[dateKey].push(appt);
                  return acc;
                }, {})
              ).map(([dateKey, dayAppointments]) => (
                <React.Fragment key={dateKey}>
                  <tr className="bg-gradient-to-r from-gold-900/30 to-transparent border-l-2 border-gold-500">
                    <td colSpan="6" className="px-3 py-4 text-zinc-100 font-bold text-lg whitespace-nowrap">
                      {(() => {
                        const d = new Date(dayAppointments[0].date);
                        const wochentag = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"][d.getDay()];
                        return `${wochentag}, ${d.toLocaleDateString("de-DE")}`;
                      })()}
                    </td>
                  </tr>
                  {dayAppointments.map((appointment) => {
                    const isBlocked = appointment.name === "BLOCKED" || appointment.service === "BLOCKED";
                    return (
                      <tr key={appointment.id} className={`hover:bg-gold-500/10 border-b border-white/5 ${isBlocked ? 'bg-red-900/20' : ''}`}>
                        <td className={`px-3 py-4 whitespace-nowrap ${isBlocked ? 'text-red-300 font-semibold' : 'text-zinc-100'}`}>{appointment.name || "N/A"}</td>
                        <td className={`px-3 py-4 whitespace-nowrap ${isBlocked ? 'text-red-300' : 'text-zinc-300'}`}>{new Date(appointment.date).toLocaleDateString("de-DE")}</td>
                        <td className={`px-3 py-4 whitespace-nowrap ${isBlocked ? 'text-red-300' : 'text-zinc-300'}`}>{appointment.weekday}</td>
                        <td className={`px-3 py-4 whitespace-nowrap ${isBlocked ? 'text-red-300' : 'text-zinc-300'}`}>{appointment.time}</td>
                        <td className={`px-3 py-4 whitespace-nowrap ${isBlocked ? 'text-red-300' : 'text-zinc-300'}`}>{appointment.service}</td>
                        <td className="px-3 py-4 whitespace-nowrap">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(appointment)}
                            title="Bearbeiten"
                            className="p-2 text-gold-400 hover:text-gold-300 bg-gold-500/15 hover:bg-gold-500/25 rounded-lg transition-all duration-200"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(appointment.id)}
                            title="Löschen"
                            className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all duration-200"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </React.Fragment>
              ))}
              {/* Falls keine Buchungen */}
              {appointments.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-3 py-8 text-center text-zinc-400 whitespace-nowrap">
                    Keine Buchungen vorhanden
                  </td>
                </tr>
              )}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden p-4 space-y-4">
              {Object.entries(
                appointments.reduce((acc, appt) => {
                  const dateKey = new Date(appt.date).toLocaleDateString("de-DE");
                  if (!acc[dateKey]) acc[dateKey] = [];
                  acc[dateKey].push(appt);
                  return acc;
                }, {})
              ).map(([dateKey, dayAppointments]) => (
                <div key={dateKey} className="space-y-3">
                  {/* Date Header */}
                  <div className="bg-gradient-to-r from-gold-900/30 to-transparent border-l-2 border-gold-500 rounded-lg p-3">
                    <h3 className="text-zinc-100 font-bold text-lg">
                      {(() => {
                        const d = new Date(dayAppointments[0].date);
                        const wochentag = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"][d.getDay()];
                        return `${wochentag}, ${d.toLocaleDateString("de-DE")}`;
                      })()}
                    </h3>
                  </div>

                  {/* Appointments */}
                  {dayAppointments.map((appointment) => {
                    const isBlocked = appointment.name === "BLOCKED" || appointment.service === "BLOCKED";
                    return (
                      <div key={appointment.id} className={`border rounded-lg p-4 backdrop-blur-xl ${isBlocked ? 'bg-red-900/20 border-red-600' : 'bg-zinc-900/80 border-gold-500/20'}`}>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-zinc-400">Name:</span>
                            <span className={`ml-2 ${isBlocked ? 'text-red-300 font-semibold' : 'text-zinc-100'}`}>{appointment.name || "N/A"}</span>
                          </div>
                          <div>
                            <span className="text-zinc-400">Zeit:</span>
                            <span className={`ml-2 ${isBlocked ? 'text-red-300' : 'text-zinc-100'}`}>{appointment.time}</span>
                          </div>
                          <div>
                            <span className="text-zinc-400">Datum:</span>
                            <span className={`ml-2 ${isBlocked ? 'text-red-300' : 'text-zinc-100'}`}>{new Date(appointment.date).toLocaleDateString("de-DE")}</span>
                          </div>
                          <div>
                            <span className="text-zinc-400">Wochentag:</span>
                            <span className={`ml-2 ${isBlocked ? 'text-red-300' : 'text-zinc-100'}`}>{appointment.weekday}</span>
                          </div>
                          <div className="col-span-2">
                            <span className="text-zinc-400">Dienstleistung:</span>
                            <span className={`ml-2 ${isBlocked ? 'text-red-300' : 'text-zinc-100'}`}>{appointment.service}</span>
                          </div>
                        </div>

                      {/* Actions */}
                      <div className="flex space-x-2 mt-4">
                        <button
                          onClick={() => handleEdit(appointment)}
                          className="flex-1 text-gold-400 hover:text-gold-300 bg-gold-500/15 hover:bg-gold-500/25 py-2 px-3 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          <span>Bearbeiten</span>
                        </button>
                        <button
                          onClick={() => handleDelete(appointment.id)}
                          className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-3 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          <span>Löschen</span>
                        </button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              ))}

              {/* No appointments message for mobile */}
              {appointments.length === 0 && (
                <div className="text-center text-zinc-400 py-8">
                  Keine Buchungen vorhanden
                </div>
              )}
            </div>
          </div>
        )}

        {/* Calendar Modal */}
        {calendarModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-2 sm:p-4" onClick={() => setCalendarModalOpen(false)}>
            <div className="bg-zinc-900/95 backdrop-blur-xl border border-gold-500/20 rounded-xl shadow-xl w-full max-w-sm sm:max-w-md mx-2 sm:mx-4 p-0 relative flex flex-col max-h-[80vh] sm:max-h-[75vh] animate-scale-in" onClick={(e) => e.stopPropagation()}>
              {/* Modal Header */}
              <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gold-500/20">
                <h3 className="text-base sm:text-lg font-semibold text-zinc-100">Datum auswählen</h3>
                <button
                  onClick={() => setCalendarModalOpen(false)}
                  className="text-zinc-400 hover:text-zinc-200 transition-all duration-200"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Calendar Content */}
              <div className="p-2 sm:p-3 flex-1 flex flex-col -ml-2">
                <div className="flex-1 flex flex-col space-y-1 sm:space-y-2">
                  {/* Month Navigation */}
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => {
                        const newDate = new Date(selectedDate);
                        newDate.setMonth(newDate.getMonth() - 1);
                        setSelectedDate(newDate);
                      }}
                      className="p-2 hover:bg-zinc-700 rounded-lg transition-all duration-200"
                    >
                      <svg className="w-5 h-5 text-zinc-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <h4 className="text-lg font-semibold text-zinc-100">
                      {selectedDate.toLocaleDateString("de-DE", { month: "long", year: "numeric" })}
                    </h4>
                    <button
                      onClick={() => {
                        const newDate = new Date(selectedDate);
                        newDate.setMonth(newDate.getMonth() + 1);
                        setSelectedDate(newDate);
                      }}
                      className="p-2 hover:bg-zinc-700 rounded-lg transition-all duration-200"
                    >
                      <svg className="w-5 h-5 text-zinc-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>

                  {/* Weekday Headers */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'].map(day => (
                      <div key={day} className="text-center text-sm font-medium text-gold-400 py-2">
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar Grid */}
                  <div className="grid grid-cols-7 gap-1 flex-1 overflow-hidden">
                    {(() => {
                      const year = selectedDate.getFullYear();
                      const month = selectedDate.getMonth();
                      const firstDay = new Date(year, month, 1);
                      const lastDay = new Date(year, month + 1, 0);
                      const startDate = new Date(firstDay);
                      startDate.setDate(startDate.getDate() - firstDay.getDay());

                      const days = [];
                      const today = new Date();

                      for (let i = 0; i < 42; i++) {
                        const date = new Date(startDate);
                        date.setDate(startDate.getDate() + i);

                        const isCurrentMonth = date.getMonth() === month;
                        const isToday = date.toDateString() === today.toDateString();
                        const isSelected = date.toDateString() === selectedDate.toDateString();
                        const isBlocked = blockedDates.some(blockedDate =>
                          blockedDate.toDateString() === date.toDateString()
                        );
                        const isPast = date < today && !isToday;

                        days.push(
                          <button
                            key={i}
                            onClick={() => {
                              if (!isPast) {
                                setSelectedDate(date);
                                setCalendarModalOpen(false);
                                // Lade gebuchte und gesperrte Slots für diesen Tag
                                const startOfDay = new Date(date);
                                startOfDay.setHours(0, 0, 0, 0);
                                const nextDay = new Date(startOfDay);
                                nextDay.setDate(startOfDay.getDate() + 1);
                                supabase
                                  .from("appointments")
                                  .select("time, service")
                                  .gte("date", startOfDay.toISOString())
                                  .lt("date", nextDay.toISOString())
                                  .then(({ data: appointments }) => {
                                    setBlockedTimeSlots((appointments || []).map(a => normalizeTime(a.time)));
                                  });
                              }
                            }}
                            disabled={isPast}
                            className={`h-full w-full rounded-lg text-sm font-medium transition-all duration-200 flex items-center justify-center min-h-[40px] flex-shrink-0 ${!isCurrentMonth ? 'text-zinc-600' : ''} ${isPast ? 'text-zinc-500 cursor-not-allowed' : 'hover:bg-zinc-700 cursor-pointer'} ${isToday ? 'bg-blue-600 text-white font-bold' : ''} ${isSelected ? 'bg-gold-600 text-white font-bold' : ''} ${isBlocked ? 'bg-red-600 text-white font-bold' : ''} ${isCurrentMonth && !isPast && !isToday && !isSelected && !isBlocked ? 'text-zinc-300' : ''}`}
                          >
                            {date.getDate()}
                          </button>
                        );
                      }
                      return days;
                    })()}
                  </div>

                  {/* Legend */}
                  <div className="flex items-center justify-center space-x-4 text-sm text-zinc-400 pt-3 border-t border-gold-500/20">
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 bg-red-600 rounded"></div>
                      <span>Gesperrt</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 bg-blue-600 rounded"></div>
                      <span>Heute</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 bg-gold-600 rounded"></div>
                      <span>Ausgewählt</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end gap-2 p-2 border-t border-gold-500/20 bg-black/30 rounded-b-xl">
                <button
                  onClick={() => setCalendarModalOpen(false)}
                  className="px-3 py-1.5 bg-zinc-700/80 hover:bg-zinc-600/80 border border-zinc-600/50 hover:border-gold-500/40 text-zinc-100 rounded transition-all duration-200 text-sm"
                >
                  Schließen
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPanel;
